import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { analyzeECGImage } from '../../services/ecgService';
import Layout from '../../components/layout/Layout';
import {
  FaHeart,
  FaExclamationTriangle,
  FaHistory,
  FaDownload,
} from 'react-icons/fa';
import { Line } from 'react-chartjs-2';
import { jsPDF } from 'jspdf';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const ECGAnalysisPage = () => {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [noiseAlert, setNoiseAlert] = useState(false);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setResult(null);
    setNoiseAlert(false);

    if (!file) {
      setError('Por favor, selecione um arquivo de imagem para upload.');
      setLoading(false);
      return;
    }

    try {
      const analysisResult = await analyzeECGImage(file);
      setResult(analysisResult);
      setAnalysisHistory((prev) => [
        ...prev,
        { date: new Date().toLocaleDateString(), result: analysisResult },
      ]);

      if (analysisResult.noiseLevel > 10) {
        setNoiseAlert(true);
      }
    } catch {
      setError('Erro ao processar o ECG. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const getPatternDescriptions = (patterns) => {
    const patternDescriptions = {
      'Bradicardia Sinusal': 'A frequência cardíaca é mais lenta que o normal.',
      'Taquicardia Sinusal':
        'A frequência cardíaca é mais rápida que o normal.',
      'Fibrilação Atrial': 'Batimentos cardíacos irregulares e descoordenados.',
      'Taquicardia Ventricular':
        'Batimentos cardíacos rápidos iniciados nos ventrículos.',
      'Bloqueio de Ramo Direito':
        'Atraso na condução elétrica pelo ramo direito do coração.',
      'Bloqueio de Ramo Esquerdo':
        'Atraso na condução elétrica pelo ramo esquerdo do coração.',
      'Flutter Atrial': 'Batimentos rápidos e regulares originados nos átrios.',
      'Fibrilação Ventricular':
        'Contrações rápidas e desorganizadas dos ventrículos.',
      'Isquemia Miocárdica':
        'Redução do fluxo sanguíneo para o músculo cardíaco.',
      'Infarto Agudo do Miocárdio':
        'Bloqueio no fluxo sanguíneo que causa dano ao coração.',
      'Síndrome de QT Longo':
        'Prolongamento da recuperação elétrica após cada batimento cardíaco.',
    };

    return patterns.map((pattern) => {
      const description =
        patternDescriptions[pattern] || 'Descrição não disponível';
      return `${pattern}: ${description}`;
    });
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text('Relatório de Análise de ECG', 105, 20, { align: 'center' });

    doc.setFontSize(14);
    doc.text(`Picos Detectados: ${result.peaksDetected}`, 20, 40);
    doc.text(`Nível de Ruído: ${result.noiseLevel}`, 20, 50);

    doc.setFontSize(16);
    doc.text('Resumo da Análise', 20, 70);
    doc.setFontSize(12);
    doc.text(result.summary, 20, 80, { maxWidth: 170 });

    if (result.detectedPatterns && result.detectedPatterns.length > 0) {
      doc.setFontSize(14);
      doc.text('Padrões Detectados:', 20, 110);
      const descriptions = getPatternDescriptions(result.detectedPatterns);
      descriptions.forEach((desc, index) => {
        doc.text(`${index + 1}. ${desc}`, 20, 120 + index * 10);
      });
    }

    doc.save('relatorio_ecg.pdf');
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div
          className="bg-white p-6 shadow-lg rounded-lg"
          data-testid="analysis-form"
        >
          <h2 className="text-3xl font-bold text-primary mb-6">
            Análise de ECG com Inteligência Artificial
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="text-left">
              <label className="block text-gray-700 font-bold mb-2">
                Faça o upload do ECG (Imagem):
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="w-full border rounded-md"
                data-testid="file-input"
              />
            </div>
            {error && (
              <div className="text-red-500 text-sm" data-testid="error-message">
                {error}
              </div>
            )}
            <button
              type="submit"
              className="w-full bg-primary text-white font-bold py-3 px-4 rounded-md hover:bg-primary-hover transition duration-300"
              disabled={loading}
              data-testid="submit-button"
            >
              {loading ? 'Analisando...' : 'Enviar e Analisar'}
            </button>
          </form>

          {result && (
            <div className="mt-8" data-testid="analysis-result">
              <h3 className="font-bold text-secondary">
                Resultado da Análise:
              </h3>
              <div className="mt-4 bg-background p-6 rounded-lg shadow-lg">
                <div className="flex items-center justify-center">
                  <FaHeart className="text-primary text-4xl" />
                  <span className="text-lg font-semibold ml-4">
                    Picos Detectados: {result.peaksDetected}
                  </span>
                </div>
                <p className="mt-4 text-gray-700">{result.summary}</p>
                {result.detectedPatterns && (
                  <div className="mt-6">
                    <h4 className="text-xl font-bold">Padrões Detectados:</h4>
                    <ul className="list-disc ml-6 mt-2">
                      {getPatternDescriptions(result.detectedPatterns).map(
                        (desc, index) => (
                          <li key={index} className="text-gray-700">
                            {desc}
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                )}
              </div>

              {noiseAlert && (
                <div
                  className="mt-6 bg-red-100 text-red-700 p-4 rounded-lg flex items-center justify-center"
                  data-testid="noise-alert"
                >
                  <FaExclamationTriangle className="text-red-600 mr-2" />
                  Nível de ruído alto detectado. A análise pode não ser precisa.
                </div>
              )}

              <button
                onClick={handleDownloadPDF}
                className="mt-6 w-full bg-secondary text-white font-bold py-3 px-4 rounded-md hover:bg-secondary-hover transition duration-300 flex items-center justify-center"
                data-testid="download-pdf-button"
              >
                <FaDownload className="mr-2" />
                Baixar Relatório (PDF)
              </button>
            </div>
          )}
        </div>

        <div className="space-y-8">
          <div
            className="bg-white p-6 shadow-lg rounded-lg"
            data-testid="analysis-history"
          >
            <h3 className="font-bold text-secondary mb-4 flex items-center">
              <FaHistory className="mr-2" />
              Histórico de Análises
            </h3>
            {analysisHistory.length > 0 ? (
              <ul className="space-y-4">
                {analysisHistory.map((entry, index) => (
                  <li
                    key={index}
                    className="p-4 bg-gray-100 rounded-lg shadow-sm"
                    data-testid={`history-entry-${index}`}
                  >
                    <span className="font-semibold">{entry.date}</span>:
                    {entry.result.summary}
                    {entry.result.detectedPatterns &&
                      entry.result.detectedPatterns.length > 0 && (
                        <div className="mt-2">
                          <strong>Padrões Detectados:</strong>{' '}
                          {entry.result.detectedPatterns.join(', ')}
                        </div>
                      )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-600">
                Nenhuma análise realizada até o momento.
              </p>
            )}
          </div>

          {analysisHistory.length > 0 && (
            <div
              className="bg-white p-6 shadow-lg rounded-lg"
              data-testid="peaks-evolution-chart"
            >
              <h3 className="font-bold text-secondary mb-4">
                Evolução dos Picos Detectados
              </h3>
              <LineChart data={analysisHistory} />
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

const LineChart = ({ data }) => {
  const chartData = {
    labels: data.map((entry) => entry.date),
    datasets: [
      {
        label: 'Picos Detectados',
        data: data.map((entry) => entry.result.peaksDetected),
        fill: false,
        borderColor: '#159a80',
        tension: 0.1,
      },
    ],
  };

  return (
    <div>
      <Line data={chartData} />
    </div>
  );
};
LineChart.propTypes = {
  data: PropTypes.array.isRequired,
};

export default ECGAnalysisPage;
